import Navigation from "@/components/Navigation";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Heart, Briefcase, Sparkles } from "lucide-react";
import { Helmet } from "react-helmet";
import SchemaMarkup from "@/components/SchemaMarkup";
import Breadcrumb from "@/components/Breadcrumb";
import { createArticleSchema } from "@/utils/schemaHelpers";

const HoroscopoLibra = () => {
  const articleSchema = createArticleSchema(
    "Libra: Horóscopo Diario, Características y Compatibilidad con los 12 Signos",
    "Descubre el horóscopo diario, semanal y mensual de Libra. Predicciones de amor, trabajo, salud y dinero para personas nacidas entre el 23 de septiembre y el 22 de octubre.",
    `${window.location.origin}/horoscopo-libra`
  );

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Inicio",
        "item": window.location.origin
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Horóscopo Libra",
        "item": `${window.location.origin}/horoscopo-libra`
      }
    ]
  };

  const breadcrumbItems = [
    { name: "Inicio", url: "/" },
    { name: "Horóscopo", url: "/horoscopo" },
    { name: "Libra" }
  ];

  return (
    <>
      <Helmet>
        <title>Libra: Horóscopo Diario, Características y Compatibilidad con los 12 Signos</title>
        <meta name="description" content="Descubre el horóscopo diario, semanal y mensual de Libra. Predicciones de amor, trabajo, salud y dinero para personas nacidas entre el 23 de septiembre y el 22 de octubre." />
        <link rel="canonical" href={`${window.location.origin}/horoscopo-libra`} />
        <meta property="og:title" content="Libra: Horóscopo Diario, Características y Compatibilidad" />
        <meta property="og:description" content="Descubre el horóscopo diario, semanal y mensual de Libra. Predicciones de amor, trabajo, salud y dinero." />
        <meta property="og:url" content={`${window.location.origin}/horoscopo-libra`} />
        <meta property="og:type" content="article" />
      </Helmet>
      
      <SchemaMarkup schema={articleSchema} />
      <SchemaMarkup schema={breadcrumbSchema} />
      
      <div className="min-h-screen">
        <Navigation />
        
        <main className="pt-24 pb-12 px-4">
          <div className="container mx-auto max-w-4xl">
            <Breadcrumb items={breadcrumbItems} />
            <div className="text-center mb-12">
              <div className="text-6xl mb-4">♎</div>
              <h1 className="text-5xl font-bold mb-4 text-glow">
                Libra: Horóscopo Diario, Características y Compatibilidad con los 12 Signos
              </h1>
              <p className="text-xl text-muted-foreground">23 de Septiembre - 22 de Octubre</p>
            </div>

            <section className="mb-16">
              <h2 className="text-3xl font-bold mb-6 text-primary">Tu Horóscopo Libra: Predicciones Actualizadas</h2>
              
              <Card className="p-8 gradient-celestial">
                <Tabs defaultValue="hoy" className="w-full">
                  <TabsList className="grid w-full grid-cols-3 mb-8">
                    <TabsTrigger value="hoy">Hoy</TabsTrigger>
                    <TabsTrigger value="semanal">Semanal</TabsTrigger>
                    <TabsTrigger value="mensual">Mensual</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="hoy" className="space-y-6">
                    <article data-zodiac-sign="libra" data-prediction-type="daily" data-date={new Date().toISOString().split('T')[0]}>
                      <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
                        <Heart className="w-6 h-6 text-primary" />
                        Horóscopo de Hoy para Libra
                        <time dateTime={new Date().toISOString().split('T')[0]} className="sr-only">
                          {new Date().toLocaleDateString('es-ES')}
                        </time>
                      </h3>
                      <p className="text-center text-sm text-muted-foreground mb-6">Número: 6 | Color: Rosa</p>
                      <div className="space-y-4">
                        <div data-category="love">
                          <h4 className="text-lg font-semibold mb-2 text-primary">💖 Amor</h4>
                          <p className="text-muted-foreground leading-relaxed">
                            Venus asegura armonía y encanto en tus relaciones. Es un día para el romance, la diplomacia y buscar el equilibrio. Si hay un conflicto, tu habilidad para mediar llevará a la paz.
                          </p>
                        </div>
                        <div data-category="work">
                          <h4 className="text-lg font-semibold mb-2 text-secondary">💼 Trabajo</h4>
                          <p className="text-muted-foreground leading-relaxed">
                            Las asociaciones y colaboraciones son claves hoy. Busca un socio o trabaja en equipo para obtener mejores resultados. Tu sentido de la justicia favorece proyectos de diseño o legales.
                          </p>
                        </div>
                        <div data-category="health">
                          <h4 className="text-lg font-semibold mb-2 text-accent">✨ Salud</h4>
                          <p className="text-muted-foreground leading-relaxed">
                            Enfócate en el equilibrio interno y externo. Las actividades que combinan belleza y movimiento, como la danza, serán restauradoras. Cuida tu zona lumbar.
                          </p>
                        </div>
                        <div data-category="money">
                          <h4 className="text-lg font-semibold mb-2 text-accent">💰 Dinero</h4>
                          <p className="text-muted-foreground leading-relaxed">
                            Las asociaciones y los acuerdos traen beneficios económicos. Si tienes que firmar un contrato, hazlo hoy. Busca el equilibrio en tus cuentas.
                          </p>
                        </div>
                      </div>
                    </article>
                  </TabsContent>
                  
                  <TabsContent value="semanal" className="space-y-6">
                    <div>
                      <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
                        <Briefcase className="w-6 h-6 text-secondary" />
                        Horóscopo Semanal: Tu Guía para los Próximos 7 Días
                      </h3>
                      <p className="text-muted-foreground leading-relaxed mb-4">
                        Una semana de armonía social y relaciones significativas. Los primeros días favorecen colaboraciones y sociedades. El fin de semana es ideal para eventos sociales, arte y cultivar la belleza en tu vida. Tu capacidad para crear paz será muy valorada.
                      </p>
                      <p className="text-muted-foreground leading-relaxed">
                        Venus te guía hacia experiencias estéticas y relacionales enriquecedoras. Tu sentido innato del equilibrio te ayuda a navegar situaciones delicadas con gracia. Confía en tu habilidad para ver todos los ángulos de una situación.
                      </p>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="mensual" className="space-y-6">
                    <div>
                      <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
                        <Sparkles className="w-6 h-6 text-accent" />
                        Horóscopo Mensual Libra: Enfoque y Oportunidades Clave
                      </h3>
                      <p className="text-muted-foreground leading-relaxed mb-4">
                        Este mes marca un período de crecimiento en relaciones y proyectos colaborativos. La primera mitad favorece alianzas estratégicas, contratos y compromisos importantes. Tu habilidad diplomática abre puertas y crea oportunidades valiosas.
                      </p>
                      <p className="text-muted-foreground leading-relaxed">
                        La segunda mitad del mes es ideal para expresar tu creatividad artística y buscar mayor equilibrio en todos los aspectos de tu vida. Es momento de tomar decisiones importantes que habías pospuesto, confiando en tu capacidad natural para sopesar todas las opciones.
                      </p>
                    </div>
                  </TabsContent>
                </Tabs>
              </Card>
            </section>

            <section className="mb-16">
              <h2 className="text-3xl font-bold mb-6 text-secondary">Características Profundas del Signo Libra: El Balanza y el Juez</h2>
              
              <Card className="p-8 mb-6 gradient-mystic">
                <h3 className="text-2xl font-bold mb-4">Los Pilares Astrológicos de Libra</h3>
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <h4 className="font-bold text-lg mb-2 text-primary">💨 Elemento: Aire</h4>
                    <p className="text-muted-foreground">Enfoque en las relaciones, la justicia y la estética. Libra vive en el mundo de las ideas y las conexiones.</p>
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-2 text-secondary">⚖️ Modalidad: Cardinal</h4>
                    <p className="text-muted-foreground">Define la necesidad de iniciar la armonía y el compromiso. Activo en crear balance.</p>
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-2 text-accent">♀️ Planeta Regente: Venus</h4>
                    <p className="text-muted-foreground">Rige el Equilibrio, la Diplomacia y la Belleza. El aspecto social y artístico de Venus.</p>
                  </div>
                </div>
              </Card>

              <Card className="p-8 mb-6 gradient-celestial">
                <h3 className="text-2xl font-bold mb-4">Perfil de Personalidad Libra: Fortalezas y Debilidades</h3>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="font-bold text-lg mb-3 text-primary">✨ Fortalezas</h4>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• <strong>Sociabilidad:</strong> Facilidad natural para conectar</li>
                      <li>• <strong>Diplomacia:</strong> Maestro de la mediación</li>
                      <li>• <strong>Justicia:</strong> Sentido imparcial del bien y el mal</li>
                      <li>• <strong>Sentido Estético:</strong> Apreciación profunda de la belleza</li>
                      <li>• <strong>Encanto:</strong> Magnetismo personal elegante</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-3 text-secondary">⚠️ Debilidades</h4>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• <strong>Indecisión:</strong> Dificultad para tomar decisiones</li>
                      <li>• <strong>Dependencia:</strong> Necesita validación de pareja</li>
                      <li>• <strong>Superficialidad:</strong> Énfasis excesivo en apariencias</li>
                      <li>• <strong>Evitar conflicto:</strong> Dificultad con confrontación</li>
                      <li>• <strong>Complacencia:</strong> Sacrifica necesidades propias</li>
                    </ul>
                  </div>
                </div>
                <div className="text-center p-4 bg-primary/10 rounded-lg">
                  <p className="text-xl font-bold text-primary">Mantra Clave: "Yo Equilibro"</p>
                </div>
              </Card>

              <Card className="p-8 mb-6 gradient-mystic">
                <h3 className="text-2xl font-bold mb-4">Libra en el Amor: La Búsqueda Constante de la Pareja Ideal</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Libra es el signo más orientado a las relaciones del zodiaco. Busca la pareja perfecta con quien compartir una vida de belleza, armonía y equilibrio. El romance para Libra debe ser elegante, refinado y lleno de consideración mutua. Valora la estética del amor tanto como la emoción.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Necesita una relación donde exista igualdad, respeto y apreciación mutua. Puede perder su identidad en la pareja si no tiene cuidado. Su desafío es encontrar el balance entre complacer a su pareja y mantener su propia autonomía. Cuando está en equilibrio, es el compañero más considerado y armonioso.
                </p>
              </Card>

              <Card className="p-8 gradient-celestial">
                <h3 className="text-2xl font-bold mb-4">Libra en el Trabajo y Finanzas: Mediación y Asuntos Legales</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Libra destaca en derecho, mediación, relaciones públicas, diseño, moda, arte, decoración y cualquier campo que requiera sentido estético y habilidad diplomática. Su capacidad para ver múltiples perspectivas lo hace invaluable en negociaciones y resolución de conflictos.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Con las finanzas, Libra aprecia el lujo y la calidad, pero también valora la justicia económica. Puede gastar en belleza y experiencias sociales. Excelente para negocios relacionados con arte, lujo y servicios que requieran elegancia y buen gusto.
                </p>
              </Card>
            </section>

            <section>
              <h2 className="text-3xl font-bold mb-6 text-accent">Compatibilidad de Libra con los Otros Signos Zodiacales</h2>
              
              <Card className="p-8 mb-6 gradient-mystic">
                <h3 className="text-2xl font-bold mb-4">💚 Tu Match Perfecto</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-bold text-lg mb-2">♊ Géminis y ♒ Acuario</h4>
                    <p className="text-muted-foreground">
                      Los signos de aire comparten amor por las ideas y la comunicación refinada. Con Géminis hay diversión intelectual constante, mientras que con Acuario existe una visión compartida de justicia social y progreso.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-2">♌ Leo y ♐ Sagitario</h4>
                    <p className="text-muted-foreground">
                      Los signos de fuego aportan la pasión que complementa la naturaleza romántica de Libra. Leo trae glamour y drama, mientras que Sagitario añade aventura y filosofía a la relación.
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-8 gradient-celestial">
                <h3 className="text-2xl font-bold mb-4">⚡ Los Desafíos y Lecciones de Libra</h3>
                <p className="text-muted-foreground mb-4">
                  El valor de su propia decisión es el gran desafío de Libra. Aries, su signo opuesto, le enseña sobre acción independiente y la valentía de decidir sin buscar aprobación externa. Los signos de agua (Cáncer, Escorpio, Piscis) lo desafían con profundidad emocional.
                </p>
                <p className="text-muted-foreground">
                  Los signos de tierra (Tauro, Virgo, Capricornio) pueden frustrar a Libra con su enfoque práctico sobre la estética, pero le enseñan sobre la importancia de la sustancia sobre el estilo y la necesidad de tomar decisiones firmes.
                </p>
              </Card>
            </section>
          </div>
        </main>
      </div>
    </>
  );
};

export default HoroscopoLibra;